import { IllegalArgumentError } from '@core/errors/errors-core';
import { Task } from '@core/services/task';

import { PatchData, PatchId, PatchNature } from '@archipad-js/core/patch';

import {
    AppDBValue,
    getDB,
    LocalPatchData,
    startsWithIDBKeyRange,
} from '@ui-archipad/helpers/indexedDB';
import { makeProgressHandler, ProgressObserver } from '@core/tasks/progress';
import { LocalProjectInfo } from '@archipad/backend/project/projectInfo';
import { ProjectRepository } from '@archipad/backend/project/baseProjectContext';
import { CURRENT_PATCH, TempPatch } from '@archipad/backend/archiweb/synchroDriver';

export class ProjectRepositoryIndexedDB implements ProjectRepository {
    async listProjects(_task: Task | null, userId: number): Promise<{
    projectId: string;
    info: LocalProjectInfo;
    }[]> {
        if (!userId) {
            throw new IllegalArgumentError("No user Id provided");
        }

        const db = await getDB();

        const userKeyPrefix = `${userId}|`;
        const userKeyPrefixKeyRange = startsWithIDBKeyRange(userKeyPrefix);

        const projects = await db.getAll('Projects', userKeyPrefixKeyRange);
        return projects;
    }

    async removeProject(_task: Task | null, userId: number, projectId: string, patchesOnly?: boolean): Promise<void> {
        if (!userId || !projectId) {
            throw new IllegalArgumentError("No userId or projectId provided");
        }

        const db = await getDB();

        const projectIdPrefix = `${userId}|${projectId}`;

        const patchIdPrefixKeyRange = startsWithIDBKeyRange(projectIdPrefix);
        await db.delete('ProjectPatches', patchIdPrefixKeyRange);

        if (patchesOnly) {
            return;
        }

        const projectIdPrefixKeyRange = IDBKeyRange.only(projectIdPrefix);
        await db.delete('Projects', projectIdPrefixKeyRange);
    }

    async getProject(_task: Task | null, userId: number, projectId: string): Promise<AppDBValue<'Projects'> | null> {
        if (!userId || !projectId) {
            throw new IllegalArgumentError("No userId or projectId provided");
        }

        const db = await getDB();

        const projectIdPrefix = `${userId}|${projectId}`;

        const project = db.get('Projects', projectIdPrefix);
        return project ?? null;
    }

    async setProjectInfo(userId: number, projectId: string, projectInfo: LocalProjectInfo): Promise<void> {
        if (!userId || !projectId) {
            throw new IllegalArgumentError("No userId or projectId provided");
        }

        const db = await getDB();
        await db.put('Projects', {
            projectId: `${userId}|${projectId}`,
            info: projectInfo,
        });
    }

    async listProjectPatches(_task: Task | null, userId: number, projectId: string): Promise<ReadonlySet<string>> {
        if (!userId || !projectId) {
            throw new IllegalArgumentError("No userId or projectId provided");
        }

        const db = await getDB();

        const patchIdPrefix = `${userId}|${projectId}|`;
        const patchIdPrefixKeyRange = startsWithIDBKeyRange(patchIdPrefix);

        const patchKeys = await db.getAllKeys('ProjectPatches', patchIdPrefixKeyRange);
        const patchIds = patchKeys.map((patchKeys) => patchKeys.substr(patchIdPrefix.length));

        return new Set(patchIds);
    }

    async getAllCurrentPatches(): Promise<{ patchId: string; data: TempPatch; nature: PatchNature; userId: string; projectId: string }[]> {
        const db = await getDB();
        const allKeys = await db.getAllKeys('ProjectPatches');

        const keysWithCurrentPatch = allKeys.filter((key) => key.endsWith(CURRENT_PATCH)).map((key) => key);


        return Promise.all(keysWithCurrentPatch.map(async (patchKey) => {
            const patchData = await db.get('ProjectPatches', patchKey);
            if (!patchData) {
                return null;
            }
            const [userId, projectId, patchId] = patchKey.split('|');
            return {
                patchId,
                data: patchData.data as TempPatch,
                nature: patchData.nature,
                userId: userId,
                projectId,
            };
        }));
    }

    /**
     * Remove a patch from the project cache
     * 
     * ***Is forbidden to delete a dumpPatch*** via this API instead use {@link removeDumpPatch}
     */
    async removePatchData(userId: number, projectId: string, patchId: string): Promise<void> {
        if (!userId || !projectId || !patchId) {
            throw new IllegalArgumentError("No userId or projectId or patchId provided");
        }

        const db = await getDB();

        const patchKey = `${userId}|${projectId}|${patchId}`;
        const patchData = await db.get('ProjectPatches', patchKey);
        if (!patchData) {
            return;
        }
        if (patchData.nature === PatchNature.DumpPatch || patchData.nature === PatchNature.LinkedEmptyPatch) {
            throw new IllegalArgumentError("Illegal attempt to delete a dumpPatch via removePatchData()");
        }
        return this._removePatchData(patchKey);
    }
    
    protected async _removePatchData(patchKey: string): Promise<void> {
        const db = await getDB();
        await db.delete('ProjectPatches', patchKey);
    }

    protected async _removePatchesData(patchesKeys: string[]): Promise<void> {
        const db = await getDB();
        const tx = db.transaction('ProjectPatches', 'readwrite');

        const deleteActions = patchesKeys.map((patchKey) => tx.store.delete(patchKey));
        await Promise.all([
            ...deleteActions,
            tx.done,
        ]);
    }
    /**
     * @returns Return all deleted patches ID of the dumpPatch
     */
    async removeDumpPatch(userId: number, projectId: string): Promise<string[]> {
        if (!userId || !projectId) {
            throw new IllegalArgumentError("No userId or projectId or patchId provided");
        }

        const linkedDumpPatchesId = await this.getPatchesIdByNature(userId, projectId, PatchNature.LinkedEmptyPatch);
        const dumpPatchesId = await this.getPatchesIdByNature(userId, projectId, PatchNature.DumpPatch);
        const allPatchesId = Array.from([...linkedDumpPatchesId, ...dumpPatchesId]);
        const patchesKeys = allPatchesId.map((patchId) => `${userId}|${projectId}|${patchId}`);
        await this._removePatchesData(patchesKeys);
        return allPatchesId;
    }

    async setPatchData<T extends PatchId>(userId: number, projectId: string, patchId: T, patchData: LocalPatchData<T>, patchNature: PatchNature): Promise<void> {
        if (!userId || !projectId || !patchId) {
            throw new IllegalArgumentError("No userId or projectId or patchId provided");
        }

        const db = await getDB();

        const patchKey = `${userId}|${projectId}|${patchId}`;
        await db.put('ProjectPatches', {
            patchId: patchKey,
            data: patchData,
            nature: patchNature,
        });
    }

    async setPatchesData(userId: number, projectId: string, progress: ProgressObserver, patchesData: PatchData[]): Promise<void> {
        if (!userId || !projectId) {
            throw new IllegalArgumentError("No userId or projectId provided");
        }
        const patchProgress = makeProgressHandler(progress);
        patchProgress.total(patchesData.length);

        const db = await getDB();
        const transaction = db.transaction('ProjectPatches', 'readwrite');
        const projectPatchesStore = transaction.store;
        const insertPromises: Promise<any>[] = patchesData.map(async (patchData, index) => {
            const patchKey = `${userId}|${projectId}|${patchData.patchId}`;
            try {
                return projectPatchesStore.put({
                    patchId: patchKey,
                    data: { patch: patchData.patch, attachmentManifest: patchData.attachmentManifest },
                    nature: patchData.patch[0].nature ? patchData.patch[0].nature : 'patchData',
                });
            } finally {
                patchProgress.units(index + 1);
            }
        });

        await Promise.all([...insertPromises, transaction.done]);
    }


    async getPatchData<T extends PatchId>(userId: number, projectId: string, patchId: T): Promise<{ patch: LocalPatchData<T>, nature: PatchNature } | null> {
        if (!userId || !projectId || !patchId) {
            throw new IllegalArgumentError("No userId or projectId or patchId provided");
        }

        const db = await getDB();

        const patchKey = `${userId}|${projectId}|${patchId}`;
        const patch = await db.get('ProjectPatches', patchKey);

        if (!patch) {
            return null;
        }

        return {
            patch: patch.data as LocalPatchData<T>,
            nature: patch.nature,
        };
    }

    /**
     * Raw read of the local temp patch (`current`): no integrity or version check.
     */
    async getTempPatch(userId: number, projectId: string): Promise<TempPatch | null> {
        const patchInfo = await this.getPatchData(userId, projectId, CURRENT_PATCH);
        return patchInfo ? patchInfo.patch : null;
    }

    async setTempPatch(userId: number, projectId: string, tempPatch: TempPatch): Promise<void> {
        return this.setPatchData(userId, projectId, CURRENT_PATCH, tempPatch, PatchNature.TempPatch);
    }

    async removeTempPatch(userId: number, projectId: string): Promise<void> {
        return this.removePatchData(userId, projectId, CURRENT_PATCH);
    }

    async getPatchesIdByNature(userId: number, projectId: string, nature: PatchNature): Promise<Set<string>> {
        if (!userId || !projectId || !nature) {
            throw new IllegalArgumentError("No userId or projectId or nature provided");
        }
        const db = await getDB();

        const patchIdPrefix = `${userId}|${projectId}|`;

        const patchIdPrefixKeyRange = startsWithIDBKeyRange(patchIdPrefix);

        let patches = await db.getAll('ProjectPatches', patchIdPrefixKeyRange);

        patches = patches.filter((patch) => patch.nature === nature);

        //Extract patchId
        const patchIds = patches
            .map((patch) => patch.patchId)
            .map((patchKeys) => patchKeys.substr(patchIdPrefix.length));

        return new Set(patchIds);
    }
}
